import React, { useState, useRef, useEffect } from 'react';
import 'altcha';

/**
 * Get the display value from an option (supports both string and object formats).
 */
function getOptionValue(option) {
  if (typeof option === 'string') return option;
  return option?.value || '';
}

function DynamicForm({ schema, onSubmit, onCancel, loading = false, altchaChallengeUrl, altchaEnabled = false, onAltchaPayloadChange, validateBasicFields, onBasicFieldError }) {
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [altchaPayload, setAltchaPayload] = useState(null);
  const [altchaError, setAltchaError] = useState(null);
  const altchaRef = useRef(null);

  // Handle ALTCHA widget events
  useEffect(() => {
    if (!altchaEnabled) return;
    const widget = altchaRef.current;
    if (!widget) return;

    const handleStateChange = (ev) => {
      if (ev.detail) {
        if (ev.detail.state === 'verified') {
          setAltchaPayload(ev.detail.payload);
          setAltchaError(null);
          if (onAltchaPayloadChange) onAltchaPayloadChange(ev.detail.payload);
        } else if (ev.detail.state === 'error') {
          setAltchaPayload(null);
          setAltchaError('Errore verifica CAPTCHA. Riprova.');
          if (onAltchaPayloadChange) onAltchaPayloadChange(null);
        } else if (ev.detail.state === 'unverified') {
          setAltchaPayload(null);
          if (onAltchaPayloadChange) onAltchaPayloadChange(null);
        }
      }
    };

    widget.addEventListener('statechange', handleStateChange);
    return () => widget.removeEventListener('statechange', handleStateChange);
  }, [altchaEnabled, onAltchaPayloadChange]);

  const handleChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    setResponses(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, option] };
      } else {
        return { ...prev, [fieldId]: current.filter(o => o !== option) };
      }
    });
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    for (const field of schema) {
      if (field.required) {
        const value = responses[field.id];
        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
          newErrors[field.id] = 'Questo campo è obbligatorio';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = () => {
      // Always validate form fields first to set errors
      const hasFormErrors = !validate();

      let hasBasicErrors = false;
      if (validateBasicFields) {
        const basicErrors = validateBasicFields();
        if (basicErrors && Object.keys(basicErrors).length > 0) {
          if (onBasicFieldError) {
            onBasicFieldError(basicErrors);
          }
          hasBasicErrors = true;
        }
      }

      // Validate ALTCHA if enabled
      if (altchaEnabled && !altchaPayload) {
        setErrors({ altcha: 'Completa la verifica CAPTCHA' });
        return;
      }

      if (hasBasicErrors || hasFormErrors) {
        return;
      }

      onSubmit(responses);
    };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            id={field.id}
            value={responses[field.id] || ''}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors[field.id] ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={field.id}
            value={responses[field.id] || ''}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={4}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors[field.id] ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, idx) => {
              const optionValue = getOptionValue(option);
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={field.id}
                    value={optionValue}
                    checked={responses[field.id] === optionValue}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{optionValue}</span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, idx) => {
              const optionValue = getOptionValue(option);
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(responses[field.id] || []).includes(optionValue)}
                    onChange={(e) => handleCheckboxChange(field.id, optionValue, e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{optionValue}</span>
                </label>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (!schema || schema.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {schema.map((field) => (
        <div key={field.id}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-2">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {renderField(field)}
          {errors[field.id] && (
            <p className="mt-1 text-sm text-red-600">{errors[field.id]}</p>
          )}
        </div>
      ))}

      {/* ALTCHA Widget - Self-hosted CAPTCHA */}
      {altchaEnabled && altchaChallengeUrl && (
        <div className="flex flex-col items-center gap-2 pt-4 border-t">
          <altcha-widget
            ref={altchaRef}
            challengeurl={altchaChallengeUrl}
            hidefooter
            strings={JSON.stringify({
              label: "Non sono un robot",
              verifying: "Verifica in corso...",
              verified: "Verificato",
              error: "Verifica fallita. Riprova.",
              expired: "Verifica scaduta. Riprova.",
              waitAlert: "Verifica in corso... attendere."
            })}
          />
          {altchaError && (
            <p className="text-red-500 text-sm">{altchaError}</p>
          )}
          {errors.altcha && (
            <p className="text-red-500 text-sm">{errors.altcha}</p>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 py-3 text-white rounded-lg font-medium transition"
          style={{ backgroundColor: loading ? '#93C5FD' : '#0E60C6' }}
          onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#0B4FA3')}
          onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#0E60C6')}
        >
          {loading ? 'Invio in corso...' : 'Invia Iscrizione'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium disabled:opacity-50"
        >
          Annulla
        </button>
      </div>
    </div>
  );
}

export default DynamicForm;
